import { type Delta, diff as jsonDiff } from "jsondiffpatch";

// NOTE: jsondiffpatch only text-diffs when a `diffMatchPatch` implementation is
// configured (v0.7 makes it opt-in). We don't configure one, so long string
// fields (FHIR `note`, narrative `text`, …) stay whole `[old, new]` pairs — no
// unidiff blobs the walker would mishandle. Array-move detection is on by
// default, so the walker still handles the move shape explicitly below.

/**
 * A single flattened change between two versions of a FHIR resource.
 * Derived from a jsondiffpatch {@link Delta} so both the clinical and
 * engineering views render from one source of truth.
 */
export interface ChangeEntry {
  /** Structured path to the changed field, e.g. `["dosageInstruction", 0, "text"]`. */
  path: (string | number)[];
  kind: "added" | "removed" | "modified";
  before?: unknown;
  after?: unknown;
}

/** Compute the raw jsondiffpatch delta between two resource versions. */
export const computeDelta = (before: unknown, after: unknown): Delta =>
  jsonDiff(before, after);

const isArrayDelta = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  (value as Record<string, unknown>)._t === "a";

/**
 * Walk a jsondiffpatch delta into a flat list of {@link ChangeEntry}.
 *
 * Handles the documented delta shapes:
 * - `[newVal]` added, `[oldVal, newVal]` modified, `[oldVal, 0, 0]` removed
 * - nested object deltas (recursed)
 * - array deltas (`{ _t: "a", ... }`) with `"n"` (added/modified at index n)
 *   and `"_n"` (removed/moved from original index n) keys
 */
export const flattenDelta = (delta: Delta | undefined): ChangeEntry[] => {
  const changes: ChangeEntry[] = [];

  const walk = (node: unknown, path: (string | number)[]): void => {
    if (Array.isArray(node)) {
      if (node.length === 1) {
        changes.push({ path, kind: "added", after: node[0] });
      } else if (node.length === 2) {
        changes.push({
          path,
          kind: "modified",
          before: node[0],
          after: node[1],
        });
      } else if (node.length === 3) {
        // 3-element deltas: [oldValue, 0, 0] = deleted; [diff, 0, 2] = text diff
        // (disabled via `differ`); ['', destIndex, 3] = array move.
        if (node[2] === 0) {
          changes.push({ path, kind: "removed", before: node[0] });
        } else if (node[2] === 3) {
          // Array reorder — position changed, not the value. Skip (not a
          // field-level change we surface).
        } else {
          // Text diff (code 2): text-diffing is disabled so this is unexpected;
          // fall back to a best-effort modified marker.
          changes.push({ path, kind: "modified", before: node[0] });
        }
      }
      return;
    }

    if (typeof node === "object" && node !== null) {
      const obj = node as Record<string, unknown>;
      const arrayDelta = isArrayDelta(obj);
      for (const key of Object.keys(obj)) {
        if (key === "_t") continue;
        if (arrayDelta && key.startsWith("_")) {
          // Removed or moved item from the original array.
          const index = Number(key.slice(1));
          walk(obj[key], [...path, index]);
        } else if (arrayDelta) {
          walk(obj[key], [...path, Number(key)]);
        } else {
          walk(obj[key], [...path, key]);
        }
      }
    }
  };

  if (delta) walk(delta, []);
  return changes;
};

/** Convenience: compute + flatten in one call. */
export const diffVersions = (before: unknown, after: unknown): ChangeEntry[] =>
  flattenDelta(computeDelta(before, after));
